/**
 * Reusable quick action cards grid component
 * Used in dashboards and landing pages for common actions
 */

import { Link } from 'react-router-dom';
import {
  ShoppingBag,
  Package,
  User,
  HelpCircle,
  Plus,
  FileText,
  Users,
  Settings,
  BarChart3,
  RefreshCw,
  Download,
  CreditCard,
  ExternalLink,
  LucideIcon,
} from 'lucide-react';

// Icon mapping for quick actions
const iconMap: Record<string, LucideIcon> = {
  'shopping-bag': ShoppingBag,
  'package': Package,
  'user': User,
  'help-circle': HelpCircle,
  'plus': Plus,
  'file-text': FileText,
  'users': Users,
  'settings': Settings,
  'bar-chart': BarChart3,
  'refresh': RefreshCw,
  'download': Download,
  'credit-card': CreditCard,
  'external-link': ExternalLink,
};

export interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: string;
  linkUrl?: string;
  onClick?: () => void;
  buttonText?: string;
  variant?: 'default' | 'primary' | 'success' | 'warning';
}

interface QuickActionsGridProps {
  actions: QuickAction[];
  columns?: 2 | 3 | 4;
  className?: string;
}

const variantStyles = {
  default: {
    bg: 'bg-gray-50 dark:bg-gray-700/50',
    hoverBg: 'group-hover:bg-gray-100 dark:group-hover:bg-gray-700',
    icon: 'text-gray-600 dark:text-gray-400',
  },
  primary: {
    bg: 'bg-primary-50 dark:bg-primary-900/30',
    hoverBg: 'group-hover:bg-primary-100 dark:group-hover:bg-primary-800/40',
    icon: 'text-primary-600 dark:text-primary-400',
  },
  success: {
    bg: 'bg-green-50 dark:bg-green-900/30',
    hoverBg: 'group-hover:bg-green-100 dark:group-hover:bg-green-800/40',
    icon: 'text-green-600 dark:text-green-400',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    hoverBg: 'group-hover:bg-amber-100 dark:group-hover:bg-amber-800/40',
    icon: 'text-amber-600 dark:text-amber-400',
  },
};

export function QuickActionsGrid({
  actions,
  columns = 3,
  className = '',
}: QuickActionsGridProps): JSX.Element {
  if (actions.length === 0) {
    return <></>;
  }

  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid grid-cols-1 ${gridCols[columns]} gap-4 ${className}`}>
      {actions.map(action => {
        const Icon = iconMap[action.icon] || HelpCircle;
        const isExternal = action.linkUrl?.startsWith('http');
        const variant = action.variant || 'primary';
        const styles = variantStyles[variant];

        const content = (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all text-center group cursor-pointer h-full">
            <div className="flex justify-center mb-4">
              <div className={`p-3 ${styles.bg} rounded-full ${styles.hoverBg} transition-colors`}>
                <Icon size={24} className={styles.icon} />
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {action.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {action.description}
            </p>
            {action.buttonText && (
              <span className="inline-flex items-center text-sm font-medium text-primary-600 dark:text-primary-400 group-hover:text-primary-700 dark:group-hover:text-primary-300">
                {action.buttonText}
                {isExternal && <ExternalLink size={14} className="ml-1" />}
              </span>
            )}
          </div>
        );

        // Handle onClick
        if (action.onClick) {
          return (
            <button
              key={action.id}
              onClick={action.onClick}
              className="text-left"
            >
              {content}
            </button>
          );
        }

        // Handle external link
        if (isExternal && action.linkUrl) {
          return (
            <a
              key={action.id}
              href={action.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {content}
            </a>
          );
        }

        // Handle internal link
        if (action.linkUrl) {
          return (
            <Link key={action.id} to={action.linkUrl}>
              {content}
            </Link>
          );
        }

        // Fallback - just render the card
        return <div key={action.id}>{content}</div>;
      })}
    </div>
  );
}

export default QuickActionsGrid;
